import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { pluralize } from '@warp-drive/utilities/string';
import { PhPlus, PhWarningCircle } from 'ember-phosphor-icons';
import Component from '@glimmer/component';
import DiscardedList from './discarded-list.gts';
import DocumentRow from './document-row.gts';
import type {
  DocumentAssetState,
  DocumentRowState,
  DocumentRowStatus,
  FlagKind,
} from './document-row.gts';
import type { DocumentType } from '../../../../types/tuition-assistance.ts';
import type CableService from '../../../../services/cable.ts';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasAssetModel from 'tio-data/models/tas-asset';
import type TasCourseModel from 'tio-data/models/tas-course';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';

export interface CategorizedUploadSignature {
  Args: {
    attachable: TasApplicationModel | TasCourseModel | TasProgramInstanceModel;
    attachableType: string;
    programTemplate: TasProgramTemplateModel;
    /** Approval-step classification (e.g. "COMPLETION_DOCUMENTS"). Drives both
     * the asset's `classification` AND the lookup of required document types. */
    classification: string;
    existingAssets?: TasAssetModel[];
    disabled?: boolean;
    onUploadComplete?: () => void;
  };
  Element: HTMLDivElement;
}

/**
 * Time the UI waits for the AI ServiceResponse before showing the "still
 * processing" fallback. The asset itself is already saved at this point — we
 * just stop spinning indefinitely.
 */
const ANALYSIS_TIMEOUT_MS = 30_000;

interface ServiceResponsePayload {
  status?: string;
  data?: { summary?: unknown };
}

type RecoveredState = Pick<DocumentRowState, 'status' | 'feedback' | 'flagKind'>;

interface ValidationOutcome {
  status: DocumentRowStatus;
  feedback: string | null;
  flagKind?: FlagKind;
  tags: Record<string, unknown>;
}

interface RecoveredTagSave {
  asset: TasAssetModel;
  tags: Record<string, unknown>;
}

type RequiredEvidenceKey =
  | 'institution'
  | 'payment_date'
  | 'payment_amount'
  | 'paid_items'
  | 'payment_method'
  | 'course_name'
  | 'grade'
  | 'completion_date'
  | 'credential_name';

/**
 * Categorized upload component. Replaces the legacy free-form upload UI for any
 * approval step whose `*_ATTACHMENT_FIELD` has a non-empty `document_types`
 * array on the program template. Steps without configured types should keep
 * using `UploadAttachments`.
 *
 * See `workspace/plans/ATTACHMENT_FEEDBACK_REALTIME_VALIDATION.md`.
 */
export default class CategorizedUpload extends Component<CategorizedUploadSignature> {
  @service declare cable: CableService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;
  @service declare store: StoreService;

  /**
   * Per-row state keyed by document type. Tracked as a plain object so a single
   * reassignment triggers re-render — Map mutations would not be reactive in
   * this glimmer version.
   */
  @tracked documentStates: Record<string, DocumentRowState> = {};

  /**
   * Soft-removed assets, kept in-memory for the lifetime of this component
   * instance to render the "Discarded:" tray (Figma B5+). Discarding here
   * destroys the attachment join on the server so the file no longer counts
   * toward the application; this list is purely UX history.
   */
  @tracked discardedAssets: TasAssetModel[] = [];

  /**
   * Extra rows added via "Add Item +". Always seeded as `general` so the
   * employee can attach supplemental documents that don't match a required
   * type. Each entry is an arbitrary key so multiples are allowed.
   */
  @tracked extraRowKeys: string[] = [];

  /**
   * Per-docType timeout handles so we can clear stale timers when the user
   * re-uploads or removes a document before the AI responds.
   */
  private analysisTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  private pendingRecoveredTagSaves = new Map<string, RecoveredTagSave>();

  private statusPriority(status: DocumentRowStatus) {
    switch (status) {
      case 'uploading':
      case 'analyzing':
        return 6;
      case 'flagged':
        return 5;
      case 'not_validated':
        return 4;
      case 'error':
        return 3;
      case 'accepted':
        return 2;
      case 'valid':
        return 1;
      default:
        return 0;
    }
  }

  private stateFromAssets(key: string, assets: TasAssetModel[]): DocumentRowState {
    if (assets.length === 0) {
      return { status: 'empty', asset: null, assets: [], assetStates: [], feedback: null };
    }

    const assetStates: DocumentAssetState[] = assets.map((asset) => ({
      asset,
      ...this.deriveStateFromAsset(asset, key),
    }));

    // Determine the overall row status. Transient states (uploading/analyzing)
    // always win so the user sees the spinner. Otherwise the *best* settled
    // status wins: a single valid doc means the category is satisfied even when
    // other uploads in the same row are flagged. The flagged banners still
    // render per-asset via `assetStates`.
    const transient = assetStates.find((s) => s.status === 'uploading' || s.status === 'analyzing');
    const valid = assetStates.find((s) => s.status === 'valid');
    const accepted = assetStates.find((s) => s.status === 'accepted');
    const primary =
      transient ??
      valid ??
      accepted ??
      assetStates.reduce((worst, current) =>
        this.statusPriority(current.status) > this.statusPriority(worst.status) ? current : worst
      );

    return {
      status: primary.status,
      asset: primary.asset,
      assets,
      assetStates,
      feedback: primary.feedback,
      flagKind: primary.flagKind,
    };
  }

  get requiredDocTypes(): DocumentType[] {
    return this.args.programTemplate.requiredDocumentTypesFor(this.args.classification);
  }

  /**
   * All admin-configured entries (label + type) for the current classification,
   * including `general` items. Used to render one row per attachment the admin
   * configured, with their custom label.
   */
  get documentEntries(): Array<{ label: string; type: DocumentType }> {
    return this.args.programTemplate.documentEntriesFor(this.args.classification);
  }

  /**
   * Map from label → type for fast lookup when the label is used as a row key.
   */
  private get labelToType(): Map<string, DocumentType> {
    const m = new Map<string, DocumentType>();
    for (const entry of this.documentEntries) {
      m.set(entry.label, entry.type);
    }
    return m;
  }

  /** The employee's full name for identity verification against AI-detected names. */
  get employeeName(): string {
    const attachable = this.args.attachable;
    // TasApplication → tasProgramInstance → employee → person
    const programInstance =
      'tasProgramInstance' in attachable
        ? (attachable as TasApplicationModel).tasProgramInstance
        : null;
    const person = programInstance?.employee?.person;
    return person?.fullName ?? '';
  }

  /** Case-insensitive check that every part of the employee name appears in the detected name. */
  namesMatch = (employeeName: string, detectedName: string): boolean => {
    const normalize = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^a-z\s]/g, '')
        .trim();
    const employeeParts = normalize(employeeName).split(/\s+/).filter(Boolean);
    const detected = normalize(detectedName);
    return employeeParts.length > 0 && employeeParts.every((part) => detected.includes(part));
  };

  private get expectedInstitutionName() {
    const attachable = this.args.attachable;
    if ('institutionName' in attachable) return attachable.institutionName;
    if ('tasProgramInstance' in attachable)
      return attachable.tasProgramInstance?.institutionName ?? '';
    if ('tasApplication' in attachable) {
      return attachable.tasApplication?.tasProgramInstance?.institutionName ?? '';
    }
    return '';
  }

  private get expectedCourseNames() {
    const attachable = this.args.attachable;
    if ('courseName' in attachable) return [attachable.courseName].filter(Boolean);
    if ('activeCourses' in attachable) {
      return attachable.activeCourses.map((course) => course.courseName).filter(Boolean);
    }
    if ('tasApplications' in attachable) {
      return attachable.tasApplications
        .flatMap((application) => application.activeCourses.map((course) => course.courseName))
        .filter(Boolean);
    }
    return [];
  }

  /**
   * Returns the row state, falling back to `empty` when nothing has been
   * uploaded for that type yet. Existing assets attached to the application
   * before this component mounted are surfaced here.
   *
   * When the key is an admin-configured label, match existing assets by their
   * employeeSelectedType (the type derived from the label→type map). Multiple
   * files may belong to the same bucket, so keep every matching live asset.
   */
  stateFor = (key: string): DocumentRowState => {
    const tracked = this.documentStates[key];
    if (tracked) return tracked;

    // For label-keyed rows, match by the underlying document type.
    const mappedType = this.labelToType.get(key);
    const matchType = mappedType ?? key;

    const existingAssets = (this.args.existingAssets ?? []).filter((a) => {
      if (a.attachmentDeletedAt !== null) return false;
      if (a.classification !== this.args.classification) return false;

      // When an asset was tagged with a specific row key (label), match only
      // the row it was uploaded under. This prevents a single `general`-typed
      // asset from appearing in every row when no document types are configured.
      const rowKey = a.uploadRowKey;
      if (rowKey) return rowKey === key;

      return a.employeeSelectedType === matchType;
    });
    return this.stateFromAssets(key, existingAssets);
  };

  get uploadedCount(): number {
    return this.documentEntries.filter((entry) => {
      const state = this.stateFor(entry.label);
      return state.status !== 'empty';
    }).length;
  }

  get flaggedCount(): number {
    return this.documentEntries.reduce(
      (count, entry) =>
        count +
        this.stateFor(entry.label).assetStates.filter((assetState) =>
          ['flagged', 'not_validated', 'error'].includes(assetState.status)
        ).length,
      0
    );
  }

  /** Map persisted tag state back to a row state when re-rendering. */
  private deriveStateFromAsset(asset: TasAssetModel, key: string): RecoveredState {
    const aiResult = this.extractAiResult(asset.assetSummary?.summary);
    if (aiResult && asset.aiValidationStatus === 'pending') {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      this.scheduleRecoveredTagSave(asset, key, outcome.tags);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }

    const displayStatus = asset.aiValidationDisplayStatus;
    const tagStatus = asset.aiValidationStatus;
    if (tagStatus === 'valid') return { status: 'valid', feedback: null };
    if (tagStatus === 'accepted') return { status: 'accepted', feedback: null };
    if (displayStatus === 'not_validated') return { status: 'not_validated', feedback: null };
    if (tagStatus === 'flagged' && aiResult) {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }
    if (tagStatus === 'flagged')
      return { status: 'flagged', feedback: asset.aiValidationFeedback ?? null };
    if (tagStatus === 'error') return { status: 'error', feedback: asset.aiValidationFeedback };
    if (tagStatus === 'pending') return { status: 'analyzing', feedback: null };
    // No AI tag yet (legacy upload): treat as a previously-uploaded file.
    return { status: 'valid', feedback: null };
  }

  private asRecord(value: unknown) {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }

  private extractAiResult(summary: unknown) {
    const summaryRecord = this.asRecord(summary);
    if (!summaryRecord) return null;

    const attributes = this.asRecord(summaryRecord['attributes']);
    const summaryPayload = this.asRecord(attributes?.['summary']) ?? summaryRecord;
    return this.asRecord(summaryPayload['result']) ?? summaryPayload;
  }

  private textValue(value: unknown) {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  }

  private numberValue(value: unknown) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private arrayOfRecords(value: unknown) {
    return Array.isArray(value)
      ? value.map((item) => this.asRecord(item)).filter((item) => item !== null)
      : [];
  }

  private comparableWords(value: string) {
    return value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((word) => word.length > 2 && !['the', 'and', 'for', 'inc', 'llc'].includes(word));
  }

  private textMatchesExpected(extracted: string, expected: string) {
    const extractedWords = new Set(this.comparableWords(extracted));
    const expectedWords = this.comparableWords(expected);
    if (expectedWords.length === 0 || extractedWords.size === 0) return false;
    return expectedWords.every((word) => extractedWords.has(word));
  }

  private maybeAddInstitutionFlags(flags: string[], extractedInstitution: string | null) {
    const expectedInstitution = this.textValue(this.expectedInstitutionName);
    if (!extractedInstitution) {
      flags.push(
        this.intl.t('tas.attachments.flag_reasons.missing_required_evidence', {
          field: this.intl.t('tas.attachments.required_evidence.institution'),
        })
      );
      return;
    }

    if (
      expectedInstitution &&
      !this.textMatchesExpected(extractedInstitution, expectedInstitution) &&
      !this.textMatchesExpected(expectedInstitution, extractedInstitution)
    ) {
      flags.push(
        this.intl.t('tas.attachments.flag_reasons.institution_mismatch', {
          detected: extractedInstitution,
          expected: expectedInstitution,
        })
      );
    }
  }

  private missingEvidenceMessage(field: RequiredEvidenceKey) {
    return this.intl.t('tas.attachments.flag_reasons.missing_required_evidence', {
      field: this.intl.t(`tas.attachments.required_evidence.${field}`),
    });
  }

  private addReceiptEvidenceFlags(flags: string[], aiResult: Record<string, unknown>) {
    const receipt = this.asRecord(aiResult['receipt']) ?? {};
    const institution = this.textValue(receipt['entity']);
    const lineItems = this.arrayOfRecords(receipt['line_items'] ?? receipt['lineItems']);
    const paymentLines = lineItems.filter((lineItem) =>
      ['payment', 'credit'].includes(
        this.textValue(lineItem['line_type'] ?? lineItem['lineType']) ?? ''
      )
    );
    const evidenceLines = paymentLines.length > 0 ? paymentLines : lineItems;

    this.maybeAddInstitutionFlags(flags, institution);
    if (
      !evidenceLines.some((lineItem) =>
        this.textValue(lineItem['line_date'] ?? lineItem['lineDate'])
      )
    ) {
      flags.push(this.missingEvidenceMessage('payment_date'));
    }
    if (
      !evidenceLines.some(
        (lineItem) => this.numberValue(lineItem['line_amount'] ?? lineItem['lineAmount']) !== null
      )
    ) {
      flags.push(this.missingEvidenceMessage('payment_amount'));
    }
    if (
      !lineItems.some((lineItem) =>
        this.textValue(
          lineItem['line_item'] ??
            lineItem['lineItem'] ??
            lineItem['line_details'] ??
            lineItem['lineDetails']
        )
      )
    ) {
      flags.push(this.missingEvidenceMessage('paid_items'));
    }
    if (
      !evidenceLines.some((lineItem) =>
        this.textValue(lineItem['form_of_payment'] ?? lineItem['formOfPayment'])
      )
    ) {
      flags.push(this.missingEvidenceMessage('payment_method'));
    }
  }

  private addGradeEvidenceFlags(flags: string[], aiResult: Record<string, unknown>) {
    const gradeOrTranscript = this.asRecord(aiResult['grade'] ?? aiResult['transcript']) ?? {};
    const institution = this.textValue(gradeOrTranscript['institution']);
    const courses = this.arrayOfRecords(gradeOrTranscript['courses']);
    const documentCourseNames = courses
      .map((course) =>
        this.textValue(
          course['course_name'] ??
            course['courseName'] ??
            course['course_detail'] ??
            course['courseDetail'] ??
            course['course']
        )
      )
      .filter((courseName) => courseName !== null);
    const expectedCourseNames = this.expectedCourseNames;

    this.maybeAddInstitutionFlags(flags, institution);
    if (documentCourseNames.length === 0) {
      flags.push(this.missingEvidenceMessage('course_name'));
    } else if (
      expectedCourseNames.length > 0 &&
      !expectedCourseNames.some((expectedCourseName) =>
        documentCourseNames.some(
          (documentCourseName) =>
            this.textMatchesExpected(documentCourseName, expectedCourseName) ||
            this.textMatchesExpected(expectedCourseName, documentCourseName)
        )
      )
    ) {
      flags.push(
        this.intl.t('tas.attachments.flag_reasons.course_mismatch', {
          detected: documentCourseNames.join(', '),
          expected: expectedCourseNames.join(', '),
        })
      );
    }
    if (!courses.some((course) => this.textValue(course['grade']))) {
      flags.push(this.missingEvidenceMessage('grade'));
    }
  }

  private addCertificateEvidenceFlags(flags: string[], aiResult: Record<string, unknown>) {
    const certificate = this.asRecord(aiResult['certificate']) ?? {};
    const institution = this.textValue(certificate['institution']);
    const credentialName = this.textValue(
      certificate['degree_or_certification'] ?? certificate['degreeOrCertification']
    );

    this.maybeAddInstitutionFlags(flags, institution);
    if (!this.textValue(certificate['completion_date'] ?? certificate['completionDate'])) {
      flags.push(this.missingEvidenceMessage('completion_date'));
    }
    if (!credentialName) {
      flags.push(this.missingEvidenceMessage('credential_name'));
    }
  }

  private addRequiredEvidenceFlags(
    flags: string[],
    actualType: DocumentType,
    aiResult: Record<string, unknown>
  ) {
    if (actualType === 'receipt') {
      this.addReceiptEvidenceFlags(flags, aiResult);
    } else if (actualType === 'grade' || actualType === 'transcript') {
      this.addGradeEvidenceFlags(flags, aiResult);
    } else if (actualType === 'certificate') {
      this.addCertificateEvidenceFlags(flags, aiResult);
    }
  }

  private scheduleRecoveredTagSave(
    asset: TasAssetModel,
    key: string,
    tags: Record<string, unknown>
  ) {
    this.pendingRecoveredTagSaves.set(`${asset.id ?? asset.uuid}:${key}`, { asset, tags });

    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.persistRecoveredTags);
  }

  private persistRecoveredTags = () => {
    const pending = [...this.pendingRecoveredTagSaves.values()];
    this.pendingRecoveredTagSaves.clear();

    for (const { asset, tags } of pending) {
      if (asset.aiValidationStatus !== 'pending') continue;

      asset.tags = tags;
      void asset.save().catch((e: unknown) => {
        console.warn('Failed to persist recovered AI validation tags', e);
      });
    }
  };

  private recoverAnalysisFromAsset = async (docType: string, asset: TasAssetModel) => {
    try {
      await asset.reload({ include: 'asset-summary' });
    } catch (e) {
      console.warn('Failed to reload asset for recovered AI validation', e);
      return false;
    }

    const aiResult = this.extractAiResult(asset.assetSummary?.summary);
    if (!aiResult) return false;

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);
    asset.tags = outcome.tags;
    try {
      await asset.save();
    } catch (e) {
      console.warn('Failed to persist recovered AI validation tags', e);
    }

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
    this.args.onUploadComplete?.();
    return true;
  };

  private clearAnalysisTimeout(docType: string) {
    const existingTimeout = this.analysisTimeouts.get(docType);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.analysisTimeouts.delete(docType);
    }
  }

  private validationOutcomeForAiResult(
    docType: string,
    aiResult: Record<string, unknown>
  ): ValidationOutcome {
    const detectedTypes =
      (aiResult['detectedDocumentTypes'] as DocumentType[] | undefined) ??
      (aiResult['detected_document_types'] as DocumentType[] | undefined) ??
      [];
    const identity = this.asRecord(aiResult['identityCheck'] ?? aiResult['identity_check']) ?? {};
    const identityNameValue = identity['detectedName'] ?? identity['detected_name'];
    const identityName = typeof identityNameValue === 'string' ? identityNameValue : null;
    const identityConfidenceValue = identity['confidence'];
    const identityConfidence =
      typeof identityConfidenceValue === 'number' ? identityConfidenceValue : 0;
    const confidenceScoreValue = aiResult['confidenceScore'] ?? aiResult['confidence_score'];
    const confidenceScore = typeof confidenceScoreValue === 'number' ? confidenceScoreValue : 0;

    const actualType = this.actualDocType(docType);
    const typeMatches =
      detectedTypes.includes(actualType) ||
      (actualType === 'grade' && detectedTypes.includes('transcript'));

    const isUnreadable = (detectedTypes as string[]).includes('unreadable');
    const isUnclassifiable =
      !isUnreadable && detectedTypes.length > 0 && detectedTypes.every((d) => d === 'general');
    const employeeName = this.employeeName;
    const nameMatches =
      identityConfidence >= 0.5 &&
      typeof employeeName === 'string' &&
      this.namesMatch(employeeName, identityName ?? '');
    const identityOk = nameMatches;

    const flags: string[] = [];
    let flagKind: FlagKind = 'content';

    if (isUnreadable) {
      flags.push(this.intl.t('tas.attachments.flag_reasons.unreadable'));
      flagKind = 'invalid_type';
    } else if (!typeMatches && !isUnclassifiable) {
      const detectedLabel = detectedTypes.join(' + ') || 'general';
      flags.push(
        this.intl.t('tas.attachments.flag_reasons.type_mismatch', {
          detected: detectedLabel,
          expected: actualType,
        })
      );
    }
    if (!identityOk && !isUnclassifiable) {
      if (identityName && employeeName && !nameMatches) {
        flags.push(
          this.intl.t('tas.attachments.flag_reasons.name_mismatch', {
            detected: identityName,
            expected: employeeName,
          })
        );
      } else {
        flags.push(this.intl.t('tas.attachments.flag_reasons.name_unverified'));
      }
    }
    if (confidenceScore > 0 && confidenceScore <= 2) {
      flags.push(this.intl.t('tas.attachments.flag_reasons.low_confidence'));
    }
    if (!isUnreadable && !isUnclassifiable && typeMatches) {
      this.addRequiredEvidenceFlags(flags, actualType, aiResult);
    }

    let status: DocumentRowStatus;
    if (isUnclassifiable) {
      status = 'not_validated';
    } else if (flags.length > 0) {
      status = 'flagged';
    } else {
      status = 'valid';
    }

    const feedback = flags.length > 0 ? flags.join('. ') : null;

    return {
      status,
      flagKind: status === 'flagged' ? flagKind : undefined,
      feedback,
      tags: {
        uploadRowKey: docType,
        employeeSelectedType: actualType,
        aiDetectedTypes: detectedTypes,
        aiValidationStatus: status === 'not_validated' ? 'flagged' : status,
        aiValidationDisplayStatus: status,
        aiValidationFeedback: feedback,
        aiTypeMatch: String(typeMatches),
        aiIdentityMatch: String(identityOk),
      },
    };
  }

  /**
   * Persist a single row's state by replacing the entire object. Required
   * because a tracked `Record` only re-renders consumers when reassigned.
   */
  private updateDocState(docType: string, partial: Partial<DocumentRowState>) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFor(docType),
        ...partial,
      },
    };
  }

  private appendAssetToDocState(
    docType: string,
    asset: TasAssetModel,
    partial: Partial<DocumentRowState>
  ) {
    const current = this.stateFor(docType);
    const assets = current.assets.some((existing) => existing === asset || existing.id === asset.id)
      ? current.assets
      : [...current.assets, asset];

    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFromAssets(docType, assets),
        asset,
        ...partial,
      },
    };
  }

  private refreshDocStateFromAssets(docType: string, assets: TasAssetModel[]) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: this.stateFromAssets(docType, assets),
    };
  }

  /**
   * Extra row keys start with "extra-"; for those the actual document type
   * persisted on the asset is always "general". For admin-configured label keys,
   * look up the corresponding type from the documentEntries map.
   */
  private actualDocType = (key: string): DocumentType => {
    if (key.startsWith('extra-')) return 'general';
    const mapped = this.labelToType.get(key);
    if (mapped) return mapped;
    // Fallback: treat the key as a literal DocumentType (legacy path).
    return key as DocumentType;
  };

  handleUpload = async (docType: string, file: unknown) => {
    this.updateDocState(docType, { status: 'uploading', feedback: null });

    // 1. Create the TasAsset record from the S3 upload details and tag it with
    //    the employee's declared type plus a pending validation marker.
    const asset = this.digitalAssets.createAssetFromS3Details(
      'tasAsset',
      file as Parameters<DigitalAssetsService['createAssetFromS3Details']>[1]
    ) as TasAssetModel;
    asset.classification = this.args.classification;
    const actualType = this.actualDocType(docType);
    asset.tags = {
      uploadRowKey: docType,
      employeeSelectedType: actualType,
      aiValidationStatus: actualType === 'general' ? 'valid' : 'pending',
      aiValidationDisplayStatus: actualType === 'general' ? 'valid' : 'pending',
      skipAiAnalysis: String(actualType === 'general'),
    };

    // 2. Link to the attachable side of the relationship. Saves any new parent
    //    records first so the polymorphic relationship has IDs to point at.
    const attachable = this.args.attachable;
    const relationshipKey = pluralize(this.args.attachableType);

    if (attachable.isNew) {
      // @ts-expect-error: dynamic relationship access on polymorphic model
      if (attachable.tasApplication?.isNew) await attachable.tasApplication.save();
      // @ts-expect-error: dynamic relationship access on polymorphic model
      if (attachable.tasProgramInstance?.isNew) await attachable.tasProgramInstance.save();
      await attachable.save();
    }
    // @ts-expect-error: dynamic relationship key access on polymorphic model
    asset[relationshipKey].push(attachable);
    attachable.tasAssets.push(asset);

    await asset.save();
    await attachable.save();

    if (actualType === 'general') {
      this.appendAssetToDocState(docType, asset, { status: 'valid', feedback: null });
      this.args.onUploadComplete?.();
      return;
    }

    // 3. Subscribe to the ActionCable channel using the asset UUID. The AI
    //    agent broadcasts on this same `request_id` once Gemini finishes.
    const channelParams = {
      channel: 'ServiceResponseChannel',
      request_id: asset.uuid,
    };
    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.handleAiResponse(docType, asset, data as ServiceResponsePayload);
      },
    });

    this.appendAssetToDocState(docType, asset, { status: 'analyzing', feedback: null });

    // 4. Fall back to "not validated" if the AI takes too long.
    //    The employee can still use the document via "Use Anyways".
    //    Clear any existing timer for this docType first (re-upload scenario).
    const existingTimeout = this.analysisTimeouts.get(docType);
    if (existingTimeout) clearTimeout(existingTimeout);

    const timeoutId = setTimeout(() => {
      void (async () => {
        const currentState = this.documentStates[docType];
        if (currentState?.status === 'analyzing' && currentState.asset) {
          this.cable.unsubscribe({
            channel: 'ServiceResponseChannel',
            request_id: currentState.asset.uuid,
          });

          const recovered = await this.recoverAnalysisFromAsset(docType, currentState.asset);
          if (!recovered && this.documentStates[docType]?.status === 'analyzing') {
            currentState.asset.tags = {
              ...currentState.asset.tags,
              employeeSelectedType:
                currentState.asset.employeeSelectedType ?? this.actualDocType(docType),
              aiValidationStatus: 'flagged',
              aiValidationDisplayStatus: 'not_validated',
            };
            void currentState.asset.save().catch((e: unknown) => {
              console.warn('Failed to persist timeout validation status', e);
            });
            this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
          }
        }
        this.analysisTimeouts.delete(docType);
      })();
    }, ANALYSIS_TIMEOUT_MS);
    this.analysisTimeouts.set(docType, timeoutId);
  };

  handleAiResponse = async (
    docType: string,
    asset: TasAssetModel,
    data: ServiceResponsePayload
  ) => {
    this.clearAnalysisTimeout(docType);

    // Always unsubscribe — the AI agent only broadcasts once per asset.
    this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });

    const summary = data?.data?.summary;
    if (!summary) {
      asset.tags = {
        ...asset.tags,
        employeeSelectedType: asset.employeeSelectedType ?? this.actualDocType(docType),
        aiValidationStatus: 'error',
        aiValidationDisplayStatus: 'error',
        aiValidationFeedback: 'No analysis available',
      };
      try {
        await asset.save();
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    // The AI response wraps the Gemini result inside a JSONAPI resource:
    //   data.summary = { id, type, attributes: { summary: { result: { ...ai fields } } } }
    // Unwrap to get the raw AI analysis. Fall back to the top-level summary
    // in case the payload arrives pre-flattened.
    const aiResult = this.extractAiResult(summary);

    if (!aiResult) {
      asset.tags = {
        ...asset.tags,
        employeeSelectedType: asset.employeeSelectedType ?? this.actualDocType(docType),
        aiValidationStatus: 'error',
        aiValidationDisplayStatus: 'error',
        aiValidationFeedback: 'No analysis available',
      };
      try {
        await asset.save();
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);

    asset.tags = outcome.tags;
    try {
      await asset.save();
    } catch (e) {
      // Persisting tags is best-effort; the UI still reflects the result. We
      // surface a console warning rather than blocking the employee.
      console.warn('Failed to persist AI validation tags', e);
    }

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);

    this.args.onUploadComplete?.();
  };

  handleRemove = async (docType: string, assetToRemove?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToRemove ?? state.asset;

    // Clean up any in-flight subscription / timer for this docType.
    const pendingTimeout = this.analysisTimeouts.get(docType);
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      this.analysisTimeouts.delete(docType);
    }
    if (asset) {
      this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
    }

    if (!asset) {
      this.updateDocState(docType, {
        status: 'empty',
        asset: null,
        assets: [],
        assetStates: [],
        feedback: null,
        flagKind: undefined,
      });
      return;
    }
    // Capture for the discarded tray BEFORE destroying the join — the asset
    // still has its filename loaded at this point.
    const discardedSnapshot = asset;
    // Mirror the legacy attachment-list removal: destroy the attachment join
    // and reload the asset so it shows as discarded for reviewers.
    // Freshly uploaded assets may not have attachments sideloaded in the store,
    // so reload first to ensure the relationship is populated.
    let attachment = asset.attachments[0];
    if (!attachment) {
      try {
        await asset.reload();
        attachment = asset.attachments[0];
      } catch (e) {
        console.warn('Failed to reload asset for attachment lookup', e);
      }
    }
    if (attachment) {
      try {
        await attachment.destroyRecord();
        await asset.reload();
      } catch (e) {
        console.warn('Failed to remove attachment', e);
      }
    }
    this.discardedAssets = [...this.discardedAssets, discardedSnapshot];
    this.refreshDocStateFromAssets(
      docType,
      state.assets.filter((existing) => existing !== asset && existing.id !== asset.id)
    );
  };

  /**
   * "Use anyways" collapses the flagged/not-validated banner into the standard
   * uploaded state without removing the asset. The validation tags persist on
   * the asset so reviewers still see the warning later.
   */
  handleUseAnyways = async (docType: string, assetToAccept?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToAccept ?? state.asset;
    if (asset) {
      asset.tags = {
        uploadRowKey: asset.uploadRowKey ?? docType,
        employeeSelectedType: asset.employeeSelectedType ?? this.actualDocType(docType),
        aiDetectedTypes: asset.aiDetectedTypes,
        aiValidationStatus: 'accepted',
        aiValidationDisplayStatus: 'accepted',
        aiValidationFeedback: asset.aiValidationFeedback,
        aiTypeMatch: String(asset.aiTypeMatch ?? ''),
        aiIdentityMatch: String(asset.aiIdentityMatch ?? ''),
      };
      try {
        await asset.save();
      } catch (e) {
        console.warn('Failed to persist accepted status', e);
      }
    }
    this.refreshDocStateFromAssets(docType, state.assets);
  };

  /**
   * Adds a new free-form upload slot. Always typed `general` so the AI agent
   * still classifies it but no required-type slot is reserved.
   */
  handleAddItem = () => {
    this.extraRowKeys = [...this.extraRowKeys, `extra-${this.extraRowKeys.length}-${Date.now()}`];
  };

  <template>
    <div class="tio-col" ...attributes>
      <p class="text-sm text-tio-gray-600">
        {{t "tas.attachments.intro"}}
      </p>
      <p class="text-xs text-tio-gray-500">
        {{t "tas.attachments.formats"}}
      </p>

      {{! Section header — "Required Documents" + Add Item link, per Figma. }}
      <div class="tio-row-between pb-1">
        <h4 class="text-sm font-semibold uppercase tracking-wider text-tio-gray-500">
          {{t "tas.attachments.required_documents"}}
        </h4>
        <button
          type="button"
          class="inline-flex items-center gap-1 text-sm font-medium text-ocean-600 hover:text-ocean-800"
          {{on "click" this.handleAddItem}}
          data-test-add-item
        >
          {{t "tas.attachments.add_item"}}
          <PhPlus @size="14" />
        </button>
      </div>

      <div class="tio-col-tight">
        {{#each this.documentEntries as |entry|}}
          <DocumentRow
            @docType={{entry.type}}
            @label={{entry.label}}
            @state={{this.stateFor entry.label}}
            @onUpload={{fn this.handleUpload entry.label}}
            @onRemove={{fn this.handleRemove entry.label}}
            @onUseAnyways={{fn this.handleUseAnyways entry.label}}
            @disabled={{@disabled}}
          />
        {{/each}}
        {{#each this.extraRowKeys as |rowKey|}}
          {{! Extra "Add Item" rows are always classified as general — the AI
            still validates them for completeness/identity but they do not
            count toward the required-types tally. Each row uses its unique key
            for state tracking so multiple extras don't share state. }}
          <DocumentRow
            @docType="general"
            @state={{this.stateFor rowKey}}
            @onUpload={{fn this.handleUpload rowKey}}
            @onRemove={{fn this.handleRemove rowKey}}
            @onUseAnyways={{fn this.handleUseAnyways rowKey}}
            @disabled={{@disabled}}
          />
        {{/each}}
      </div>

      <DiscardedList @assets={{this.discardedAssets}} />

      <div class="tio-row-between text-sm">
        <span class="text-tio-gray-600" data-test-counter>
          {{this.uploadedCount}}
          /
          {{this.documentEntries.length}}
          {{t "tas.attachments.required"}}
        </span>
        {{#if this.flaggedCount}}
          <span
            class="inline-flex items-center gap-1 font-semibold text-red-700"
            data-test-flagged-count
          >
            <PhWarningCircle @size="14" @weight="fill" />
            {{t "tas.attachments.flagged_count" count=this.flaggedCount}}
          </span>
        {{/if}}
      </div>
    </div>
  </template>
}
